
import * as XLSX from "xlsx-js-style";
import { message } from "antd";

const normalizeText = (value) => String(value ?? "").trim();

const getNestedName = (value, keys = []) => {
  if (!value) return "";
  if (typeof value === "string") return normalizeText(value);

  for (const key of keys) {
    const text = normalizeText(value?.[key]);
    if (text) return text;
  }

  return normalizeText(value?.name);
};

const getPersonName = (value) => {
  if (!value) return "";

  if (typeof value === "string") return normalizeText(value);

  if (Array.isArray(value)) {
    return value.map(getPersonName).filter(Boolean).join(", ");
  }

  const directName =
    value.name ||
    value.fullName ||
    value.memberName ||
    value.userName ||
    value.displayName ||
    value.username ||
    value.user?.name ||
    value.user?.fullName ||
    value.userId?.name ||
    value.userId?.fullName;

  if (directName) return normalizeText(directName);

  return normalizeText(
    [
      value.firstName ||
        value.userFirstName ||
        value.user?.firstName ||
        value.user?.userFirstName,
      value.lastName ||
        value.userLastName ||
        value.user?.lastName ||
        value.user?.userLastName,
    ]
      .filter(Boolean)
      .join(" ")
  );
};

const getMemberByRole = (row, roleCode) => {
  const members = Array.isArray(row?.members) ? row.members : [];
  return members.find((member) => member?.roleCode === roleCode) || null;
};

const getPersonMobile = (value) => {
  if (!value || typeof value === "string") return "";

  return normalizeText(
    value.mobileNumber ||
      value.mobile ||
      value.phone ||
      value.contactNumber ||
      value.user?.mobileNumber ||
      value.userId?.mobileNumber
  );
};

const buildChapterExcelRows = (chapters = []) =>
  (Array.isArray(chapters) ? chapters : []).map((row, index) => {
    const president =
      getMemberByRole(row, "CHAPTER_PRESIDENT") ||
      row?.chapterPresident ||
      row?.president;

    const secretary =
      getMemberByRole(row, "CHAPTER_SECRETARY") ||
      row?.chapterSecretary ||
      row?.chapterSecretory ||
      row?.secretary ||
      row?.secretory;

    return {
      "Sr No": index + 1,
      "Chapter Name": row?.chapterName || "",
      State:
        row?.stateName ||
        getNestedName(row?.location?.state, ["stateName", "StateName"]),
      Region:
        row?.regionName ||
        getNestedName(row?.location?.region, ["regionName"]),
      District:
        row?.districtName ||
        getNestedName(row?.location?.district, ["districtName"]),
      Taluka:
        row?.talukaName ||
        getNestedName(row?.location?.taluka, ["talukaName"]),
      Village:
        row?.villageName ||
        getNestedName(row?.location?.village, ["villageName"]),
      Status: row?.chapterLifecycleStatus || "",
      "Chapter President Name":
        row?.presidentName || getPersonName(president),
      "Chapter President Mobile":
        row?.presidentMobile || getPersonMobile(president),
      "Chapter Secretary Name":
        row?.secretaryName || getPersonName(secretary),
      "Chapter Secretary Mobile":
        row?.secretaryMobile || getPersonMobile(secretary),
    };
  });

export const downloadChapterExcel = (
  chapters = [],
  fileName = "chapters.xlsx"
) => {
  if (!chapters.length) {
    message.error("Chapter data not found");
    return;
  }

  message.loading({
    content: "Downloading Excel...",
    key: "excel-download",
    duration: 0,
  });

  try {
    const excelData = buildChapterExcelRows(chapters);

    const worksheet = XLSX.utils.json_to_sheet(excelData);

    worksheet["!cols"] = [
      { wch: 8 },
      { wch: 32 },
      { wch: 20 },
      { wch: 20 },
      { wch: 20 },
      { wch: 20 },
      { wch: 20 },
      { wch: 18 },
      { wch: 32 },
      { wch: 30 },
      { wch: 32 },
      { wch: 30 },
    ];

    worksheet["!rows"] = [
      { hpt: 28 },
      ...excelData.map(() => ({ hpt: 22 })),
    ];

    const range = XLSX.utils.decode_range(worksheet["!ref"]);

    const headerStyle = {
      font: {
        bold: true,
        color: { rgb: "FFFFFF" },
        sz: 12,
      },
      fill: {
        fgColor: { rgb: "4472C4" },
      },
      alignment: {
        horizontal: "center",
        vertical: "center",
      },
      border: {
        top: { style: "thin", color: { rgb: "000000" } },
        bottom: { style: "thin", color: { rgb: "000000" } },
        left: { style: "thin", color: { rgb: "000000" } },
        right: { style: "thin", color: { rgb: "000000" } },
      },
    };

    const bodyStyle = {
      alignment: {
        vertical: "center",
        horizontal: "left",
      },
      border: {
        top: { style: "thin", color: { rgb: "D9D9D9" } },
        bottom: { style: "thin", color: { rgb: "D9D9D9" } },
        left: { style: "thin", color: { rgb: "D9D9D9" } },
        right: { style: "thin", color: { rgb: "D9D9D9" } },
      },
    };

    const centerColumns = [0, 7, 9, 11];

    for (let row = range.s.r; row <= range.e.r; row++) {
      for (let col = range.s.c; col <= range.e.c; col++) {
        const cellAddress = XLSX.utils.encode_cell({
          r: row,
          c: col,
        });

        if (!worksheet[cellAddress]) continue;

        if (row === 0) {
          worksheet[cellAddress].s = headerStyle;
        } else {
          worksheet[cellAddress].s = {
            ...bodyStyle,
            alignment: {
              vertical: "center",
              horizontal: centerColumns.includes(col)
                ? "center"
                : "left",
            },
          };
        }
      }
    }

    worksheet["!autofilter"] = {
      ref: worksheet["!ref"],
    };

    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      "Chapters"
    );

    XLSX.writeFile(workbook, fileName);

    message.success({
      content: "Excel downloaded successfully",
      key: "excel-download",
      duration: 2,
    });
  } catch (err) {
    console.error(err);

    message.error({
      content: "Failed to download Excel",
      key: "excel-download",
    });
  }
};

export default downloadChapterExcel;